import React, { useEffect, useMemo, useState } from 'react';
import { SchemaField, fetchSchemaFieldsCached } from '../api/schemaField';
import SearchSelect, { SearchSelectOption } from './SearchSelect';

// Schema 字段字典 key 选择器 (参数集字段编辑 / Schema 管理字段组共用).
// 字段 key 只能从字典选 (SSOT = xtool-schema + 平台 custom 扩展);
// 字典 200+ 字段, 基于 SearchSelect 可搜索 (key / 显示名 / 单位 / 分组 均可匹配);
// 历史数据里字典外的 key 展示为 "xxx (已不在字典)", 数据保留但只能换成字典字段.

/** 字典 (模块级缓存) → { dict, byKey, groups }; 分组 = grp, 平台新增字段带 [平台扩展] 前缀. */
export function useSchemaFieldDict() {
    const [dict, setDict] = useState<SchemaField[]>([]);
    useEffect(() => {
        let alive = true;
        fetchSchemaFieldsCached()
            .then((d) => {
                if (alive) setDict(d);
            })
            .catch(() => {
                /* 静默: 下拉退化为只剩当前值 */
            });
        return () => {
            alive = false;
        };
    }, []);
    const byKey = useMemo(() => new Map(dict.map((d) => [d.fieldKey, d])), [dict]);
    const groups = useMemo(() => {
        const gs = new Map<string, SchemaField[]>();
        dict.forEach((d) => {
            const g = `${d.source === 'custom' ? '[平台扩展] ' : ''}${d.grp || '未分组'}`;
            if (!gs.has(g)) gs.set(g, []);
            gs.get(g)!.push(d);
        });
        return gs;
    }, [dict]);
    return { dict, byKey, groups };
}

/** 选项/选中项的展示文案: key · 显示名 (单位). */
function fieldLabel(d: SchemaField): string {
    return `${d.fieldKey}${d.fieldName ? ` · ${d.fieldName}` : ''}${d.unit ? ` (${d.unit})` : ''}`;
}

interface SchemaFieldKeySelectProps {
    value: string;
    byKey: Map<string, SchemaField>;
    groups: Map<string, SchemaField[]>;
    /** 布局类 (加在外层), 默认 w-full */
    className?: string;
    title?: string;
    /** 选中回调: field=字典条目 (字典外/清空时为 null), key=选中的原始值 */
    onSelect: (field: SchemaField | null, key: string) => void;
    /** 选中态只显示 key (旁边已有独立的"显示名"栏时避免重复); 下拉候选仍带中文名可搜 */
    valueKeyOnly?: boolean;
}

export function SchemaFieldKeySelect({
    value,
    byKey,
    groups,
    className,
    title,
    onSelect,
    valueKeyOnly,
}: SchemaFieldKeySelectProps) {
    // 按分组顺序平铺; 分组名并入 label, 搜索分组名也能命中
    const options = useMemo(() => {
        const opts: SearchSelectOption[] = [];
        groups.forEach((ds, g) => {
            ds.forEach((d) => opts.push({ value: d.fieldKey, label: `${fieldLabel(d)} — ${g}` }));
        });
        return opts;
    }, [groups]);
    const cur = byKey.get(value);
    const valueLabel = value
        ? cur
            ? valueKeyOnly
                ? cur.fieldKey
                : fieldLabel(cur)
            : `${value} (已不在字典)`
        : '';
    return (
        <div className={className || 'w-full'} title={title}>
            <SearchSelect
                options={options}
                value={value}
                valueLabel={valueLabel}
                placeholder="搜索字段 key / 名称…"
                className="w-full"
                onChange={(v) => onSelect(byKey.get(v) || null, v)}
            />
        </div>
    );
}
